import React, { useContext, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleSendOutlinedIcon from '@mui/icons-material/ScheduleSendOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { Divider, IconButton, ListItemIcon, Menu, MenuItem, Tooltip } from '@mui/material';

import { lensPath } from '~/pages/AskRichardUnified/lensPath';
import { useLensPinToggle } from '~/pages/AskRichardUnified/useLensPinToggle';

import useCurrentUser from '~/hooks/useCurrentUser';

import type { LensResponse } from '~/types/leLens';

import LensArchiveDialog from '../LensArchiveDialog';
import LensCertificationMenuItems, { lensCertificationActions } from '../LensCertificationMenuItems';
import LensCollaborationDialog from '../LensCollaborationDialog';
import SaveLensAsDialog from '../SaveLensAsDialog';
import { SavedViewsContext } from '../personalViews/savedViewsContext';
import LensSchedulesDialog from '../schedules/LensSchedulesDialog';
import { useReportAction } from '../useReportAction';

import LensPublicationMenuItems, { type LensPublicationMenuItemsProps } from './LensPublicationMenuItems';
import type { LensDocumentData, LensState } from './types';
import useLensDownloads, { type LensDownloadOptions } from './useLensDownloads';

type LensActionsProps = {
  detail: LensResponse;
  data?: LensDocumentData;
  state: LensState;
  asReader?: boolean;
  onRefresh?: () => void;
  refreshing?: boolean;
  onEdit?: () => void;
  editingDisabled?: boolean;
  download?: Pick<LensDownloadOptions, 'blocks' | 'tabs'>;
  publication?: {
    headDetail: LensResponse;
    draftShown: boolean;
    controls?: Omit<LensPublicationMenuItemsProps, 'publication' | 'draftShown' | 'onClose'>;
  };
};

type Dialog = 'copy' | 'schedule' | 'history' | 'archive';

export default function LensActions({
  detail,
  data,
  state,
  asReader = false,
  onRefresh,
  refreshing = false,
  onEdit,
  editingDisabled = false,
  download,
  publication,
}: LensActionsProps) {
  const { lens, access } = detail;
  const downloads = useLensDownloads({
    lensId: lens.id,
    lensVersion: lens.version,
    title: lens.title,
    state,
    blocks: download?.blocks ?? [],
    tabs: download?.tabs,
  });
  const anchor = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const pins = useLensPinToggle();
  const savedViews = useContext(SavedViewsContext);
  const pinned = pins.isPinned(lens.id);
  useReportAction('schedules', () => setDialog('schedule'), access.canRead && !publication?.draftShown);
  const open = (next: Dialog) => {
    setMenuOpen(false);
    setDialog(next);
  };
  const run = (action: () => void) => () => {
    setMenuOpen(false);
    action();
  };
  const certification =
    asReader || publication?.draftShown
      ? []
      : lensCertificationActions(access, detail.certification, detail.sharing?.audience === 'company');

  return (
    <>
      <Tooltip title="Lens actions">
        <IconButton
          aria-haspopup="menu"
          aria-label="Lens actions"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
          ref={anchor}
          size="small"
        >
          <MoreHorizIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor.current} onClose={() => setMenuOpen(false)} open={menuOpen}>
        {onEdit && !asReader && access.canEdit ? (
          <MenuItem disabled={editingDisabled} onClick={run(onEdit)}>
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Edit Lens
          </MenuItem>
        ) : null}
        {download ? (
          <MenuItem disabled={downloads.pending} onClick={run(downloads.pdf)}>
            <ListItemIcon>
              <PictureAsPdfOutlinedIcon fontSize="small" />
            </ListItemIcon>
            {downloads.pending ? 'Preparing download…' : downloads.pdfError ? 'Retry PDF download' : 'Download PDF'}
          </MenuItem>
        ) : null}
        {download?.blocks.length ? (
          <MenuItem disabled={downloads.pending} onClick={run(downloads.csv)}>
            <ListItemIcon>
              <TableChartOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Download CSV{download.blocks.length > 1 ? ' (zipped)' : ''}
          </MenuItem>
        ) : null}
        {onEdit || download ? <Divider sx={{ my: 0.5 }} /> : null}
        {publication?.controls && publication.headDetail.publication && !asReader && access.canEdit ? (
          <LensPublicationMenuItems
            {...publication.controls}
            draftShown={publication.draftShown}
            onClose={() => setMenuOpen(false)}
            publication={publication.headDetail.publication}
          />
        ) : null}
        {savedViews && !publication?.draftShown ? (
          <MenuItem key="saved-views" onClick={run(savedViews.open)}>
            <ListItemIcon>
              <BookmarkBorderOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Saved views…
          </MenuItem>
        ) : null}
        <MenuItem onClick={() => open('schedule')}>
          <ListItemIcon>
            <ScheduleSendOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Schedule email…
        </MenuItem>
        <MenuItem onClick={run(() => pins.toggle(lens.id))}>
          <ListItemIcon>
            {pinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
          </ListItemIcon>
          {pinned ? 'Unpin from rail' : 'Pin to rail'}
        </MenuItem>
        <MenuItem data-tour="copy-lens-menu-item" onClick={() => open('copy')}>
          <ListItemIcon>
            <ContentCopyOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {publication ? 'Save as…' : 'Duplicate Lens'}
        </MenuItem>
        <MenuItem onClick={() => open('history')}>
          <ListItemIcon>
            <HistoryOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Version history
        </MenuItem>
        {onRefresh ? (
          <MenuItem disabled={refreshing} onClick={run(onRefresh)}>
            <ListItemIcon>
              <RefreshIcon fontSize="small" />
            </ListItemIcon>
            {refreshing ? 'Refreshing data…' : 'Refresh data'}
          </MenuItem>
        ) : null}
        {certification.length ? <Divider key="certification-divider" sx={{ my: 0.5 }} /> : null}
        <LensCertificationMenuItems
          actions={certification}
          lensId={lens.id}
          meaning={detail.certification?.meaning}
          onDone={() => setMenuOpen(false)}
          parentMenuOpen={menuOpen}
          request={access.canRequestCertification ? detail.certification?.request : undefined}
        />
        {!asReader && access.canArchive && (
          <MenuItem onClick={() => open('archive')}>
            <ListItemIcon>
              <ArchiveOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Archive Lens
          </MenuItem>
        )}
      </Menu>
      <LensArchiveDialog
        key={dialog === 'archive' ? lens.id : 'closed'}
        lens={dialog === 'archive' ? lens : null}
        onClose={() => setDialog(null)}
      />
      <SaveLensAsDialog
        onClose={() => setDialog(null)}
        onCreated={(created) => {
          setDialog(null);
          navigate(lensPath(created.id));
        }}
        open={dialog === 'copy'}
        publicationDetail={publication?.headDetail}
        sourceLens={lens}
      />
      {dialog === 'schedule' ? (
        <LensSchedulesDialog
          filterOptions={data?.options ?? {}}
          lens={lens}
          onClose={() => setDialog(null)}
          open
          screenValues={state.filters ?? {}}
          userEmail={user?.email}
        />
      ) : null}
      <LensCollaborationDialog
        access={access}
        lens={lens}
        onClose={() => setDialog(null)}
        open={dialog === 'history'}
      />
    </>
  );
}
